/**
 * Single home for handling the WZ_ORDER_FULLY_COVERED backend error
 * (backend/src/services/wzService.js createWZOrderFullyCoveredError) — every
 * line of the order is already covered by its other WZ documents.
 *
 * This is a WARNING, never a hard block (same product decision as the
 * release-time over-delivery warning in WZDetail.tsx): the operator must
 * always have a way to generate the document anyway, with the full ordered
 * quantity, instead of being forced into hand-building one in the manual
 * wizard. showWZFullyCoveredConfirm renders that confirmation, naming the
 * actual blocking WZ document(s) and their status — e.g. "WZ/03/08/2026
 * (Szkic)" — instead of a generic "already covered" that would misleadingly
 * read as "already shipped" when the blocker is a DRAFT/CONFIRMED document
 * nobody has released yet.
 *
 * Used by frontend/src/pages/WZNew.tsx for every create entry point, so the
 * wording, data, and confirm/resend flow never drift between them.
 * getApiErrorMessage (utils/apiError.ts) is the generic
 * code -> static-translation-key mapper used for every OTHER WZ error code;
 * this one needs a status label interpolated per document and a confirm
 * action, so it gets this small dedicated helper instead of teaching the
 * generic mapper about dynamic interpolation and confirm flows for one code.
 */

import type { ReactNode } from 'react';

export interface WZFullyCoveredBlockingDocument {
  wzNumber: string;
  status: string;
}

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

interface ModalLike {
  confirm: (config: {
    title: ReactNode;
    content?: ReactNode;
    okText?: ReactNode;
    cancelText?: ReactNode;
    okType?: 'primary' | 'danger' | 'default' | 'dashed' | 'link' | 'text';
    onOk?: () => void;
    onCancel?: () => void;
  }) => void;
}

export function isWZOrderFullyCoveredError(error: unknown): boolean {
  return (error as { response?: { data?: { code?: string } } })?.response?.data?.code === 'WZ_ORDER_FULLY_COVERED';
}

function getBlockingDocuments(error: unknown): WZFullyCoveredBlockingDocument[] {
  const documents = (error as {
    response?: { data?: { documents?: WZFullyCoveredBlockingDocument[] } };
  })?.response?.data?.documents;
  return Array.isArray(documents) ? documents : [];
}

/**
 * Requires the caller's useTranslation to include BOTH the 'orders' and 'wz'
 * namespaces (the status labels live in wz:status.*).
 */
export function buildWZOrderFullyCoveredMessage(error: unknown, t: TranslateFn): string {
  const documents = getBlockingDocuments(error);

  if (documents.length === 0) {
    return t('orders:orderDetail.messages.wzFullyCovered');
  }

  const documentList = documents
    .map((doc) => `${doc.wzNumber} (${t(`wz:status.${String(doc.status || '').toLowerCase()}`)})`)
    .join(', ');

  return t('orders:orderDetail.messages.wzFullyCoveredWithDocuments', { documents: documentList });
}

/**
 * Shows the confirm dialog for a WZ_ORDER_FULLY_COVERED response and resolves
 * to the operator's choice. The caller is responsible for retrying with
 * `{ confirmFullyCovered: true }` when the result is true.
 */
export function showWZFullyCoveredConfirm(params: {
  error: unknown;
  modal: ModalLike;
  t: TranslateFn;
}): Promise<boolean> {
  const { error, modal, t } = params;
  const summary = buildWZOrderFullyCoveredMessage(error, t);

  return new Promise((resolve) => {
    modal.confirm({
      title: t('orders:orderDetail.messages.wzFullyCoveredConfirmTitle'),
      content: `${summary} ${t('orders:orderDetail.messages.wzFullyCoveredConfirmFooter')}`,
      okText: t('orders:orderDetail.messages.wzFullyCoveredConfirmOk'),
      cancelText: t('common:cancel'),
      okType: 'danger',
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
}
